import type { AxiosError, AxiosResponse } from 'axios';
import { clamp, get, noop } from 'lodash';
import { EventChannel, eventChannel } from 'redux-saga';
import { ForkEffect, call, cancel, delay, fork, put, take } from 'redux-saga/effects';

import { UNAUTHORIZED } from '@skippay/core/constants/httpStatusCodes';

import { TOKEN_EXPIRES_IN_DELAY } from 'constants/authorization';

import { loginExpiresSoonAction, sessionExpiredAction } from '../actions';
import { TOKEN_EXPIRATION_SKIPPABLE_ENDPOINTS } from '../constants';
import { setAccessTokenData, slideAccessTokenExpiration } from '../utils/accessTokenUtils';
import type { TokenData } from '../utils/accessTokenUtils';

/**
 * Creates channel and emitting values with every response
 * @return {EventChannel<AxiosResponse>}
 */
function createAxiosAuthorizedResponseChannel(): EventChannel<AxiosResponse | AxiosError> {
	return eventChannel((emitter) => {
		window.addEventListener('axios-response', (event: CustomEvent) => {
			if (!TOKEN_EXPIRATION_SKIPPABLE_ENDPOINTS.includes(event.detail.config.url)) {
				emitter(event.detail);
			}
		});

		window.addEventListener('axios-error', (event: CustomEvent) => {
			emitter(event.detail);
		});

		return () => {
			window.removeEventListener('axios-response', noop, false);
			window.removeEventListener('axios-error', noop, false);
		};
	});
}

function* expiresSoonTask(expiresIn: number): Generator<unknown, void> {
	yield put(loginExpiresSoonAction(false));
	yield delay(clamp(expiresIn * 1000 - TOKEN_EXPIRES_IN_DELAY, 0, Infinity));
	yield put(loginExpiresSoonAction(true));
}

export function* handleAuthorizedChannelTask(tokenData: TokenData): Generator {
	yield call(setAccessTokenData, tokenData);
	if (!tokenData.fullAccess) return;

	const chan = (yield call(createAxiosAuthorizedResponseChannel)) as EventChannel<
		AxiosResponse | AxiosError
	>;

	let expireSoonTask = (yield fork(expiresSoonTask, tokenData.expiresIn)) as ForkEffect<void>;

	while (true) {
		try {
			const event = (yield take(chan)) as AxiosResponse | AxiosError;

			const isUnauthorized = get(event, 'response.status') === UNAUTHORIZED;
			const isAxiosError = get(event, 'isAxiosError');

			if (isUnauthorized) {
				yield put(sessionExpiredAction());
			} else if (!isAxiosError) {
				if (expireSoonTask) {
					yield cancel(expireSoonTask);
				}

				const updatedTokenData = (yield call(slideAccessTokenExpiration)) as TokenData;

				expireSoonTask = (yield fork(
					expiresSoonTask,
					updatedTokenData.expiresIn
				)) as ForkEffect<void>;
			}
		} catch (err) {
			if (get(err, 'response.status') === UNAUTHORIZED) {
				yield put(sessionExpiredAction());
			}
		}
	}
}
